import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setCredentials } from '../features/auth/authSlice';
import { LuChartArea } from "react-icons/lu"; // A nice icon for the logo
import apiClient from '../api/apiClient';

const AuthPage = () => {
  // State to toggle between Login and Register views
  const [isLoginView, setIsLoginView] = useState(true);

  // State for form inputs
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent the form from reloading the page

    if (!isLoginView) {
      // Simple validation for registration
      if (password !== confirmPassword) {
        alert("Passwords do not match!");
        return;
      }
    }

    setLoading(true);

    try {
      const endpoint = isLoginView ? '/login' : '/register';
      const payload = isLoginView
        ? { email, password }
        : { name, email, password, password_confirmation: confirmPassword };

      const response = await apiClient.post(endpoint, payload);

      // Store the token and user in localStorage
      localStorage.setItem('auth_token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      // Set the token in axios headers for future requests
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;

      alert(`Successfully ${isLoginView ? 'logged in' : 'registered'}!`);
      // Navigate to dashboard after successful login or registration
      navigate('/dashboard');

      // Dispatch Redux action to update auth state
      dispatch(setCredentials({ user: response.data.user, token: response.data.token }));
    } catch (error) {
      console.error('Auth error:', error);
      alert(error.response?.data?.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex justify-center items-center min-h-screen bg-gray-50 p-4">
      <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-2xl shadow-lg">
        {/* Logo and Title */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center p-3 bg-blue-100 rounded-full mb-2">
            <LuChartArea className="text-blue-600" size={32} />
          </div>
          <h1 className="text-3xl font-bold text-gray-800">TradeLink</h1>
          <p className="text-gray-500">Bridge TradingView to Brokers</p>
        </div>

        {/* View Toggler (Login/Register) */}
        <div className="flex border-2 border-gray-200 rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => setIsLoginView(true)}
            className={`w-1/2 p-2 font-semibold transition-colors ${
              isLoginView ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            Login
          </button>
          <button
            type="button"
            onClick={() => setIsLoginView(false)}
            className={`w-1/2 p-2 font-semibold transition-colors ${
              !isLoginView ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            Register
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLoginView && (
            <div>
              <label className="text-sm font-medium text-gray-700">Name *</label>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your name"
              />
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-gray-700">Email *</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter your email"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Password *</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter your password"
            />
          </div>

          {/* Conditional field for Confirm Password */}
          {!isLoginView && (
             <div>
                <label className="text-sm font-medium text-gray-700">Confirm Password *</label>
                <input
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Confirm your password"
                />
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 text-white bg-blue-600 rounded-lg font-semibold hover:bg-blue-700 active:bg-blue-800 transition-transform transform active:scale-95 disabled:opacity-50"
          >
            {loading ? 'Processing...' : (isLoginView ? 'Sign In' : 'Create Account')}
          </button>
        </form>

        <p className="text-xs text-center text-gray-500">
          Secure authentication with encrypted token storage.
        </p>
      </div>
    </div>
  );
};

export default AuthPage;
